import { getInsights } from '../utils/mock';
import { BookOpen, Calendar, Clock, ArrowRight, Filter } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const Insights = () => {
  const insights = getInsights();
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Natural Gas', 'Mining', 'Renewables', 'Oil Markets', 'Policy & Regulation'];

  const featuredInsight = {
    title: 'Energy Transition Economics: 2025 Outlook',
    category: 'Special Report',
    date: '2024-12-20',
    readTime: '25 min read',
    excerpt: 'Our comprehensive annual outlook examines the economic viability of energy transition pathways, capital allocation trends, and policy frameworks shaping the next decade of global energy markets.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxlbmVyZ3klMjBkYXRhJTIwYW5hbHl0aWNzfGVufDB8fHx8MTc3MTc2MTE5NXww&ixlib=rb-4.1.0&q=85'
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1F7A4F] via-[#2C6E73] to-[#284A86] text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 whitespace-nowrap">Market Insights & Analysis</h1>
            <p className="text-base lg:text-lg text-white/90 leading-relaxed">
              Expert commentary, research publications, and intelligence briefings on the trends shaping global energy markets.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Insight */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-lg overflow-hidden border-2 border-blue-200 shadow-xl">
            <div className="grid lg:grid-cols-2 gap-0">
              <div className="p-12 flex flex-col justify-center">
                <div className="inline-block px-4 py-2 bg-blue-700 text-white text-sm font-semibold rounded-full mb-6 w-fit">
                  Featured Report
                </div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                  {featuredInsight.title}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {featuredInsight.excerpt}
                </p>
                <div className="flex items-center space-x-6 text-sm text-slate-600 mb-8">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>{featuredInsight.date}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4" />
                    <span>{featuredInsight.readTime}</span>
                  </div>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-6 py-3 bg-blue-700 text-white font-semibold rounded-md hover:bg-blue-800 transition-all duration-200 w-fit"
                >
                  Request Full Report
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
              <div className="h-full min-h-[400px]">
                <img
                  src={featuredInsight.image}
                  alt={featuredInsight.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-slate-50 border-y border-slate-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4 overflow-x-auto">
            <Filter className="w-5 h-5 text-slate-600 flex-shrink-0" />
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full font-medium text-sm whitespace-nowrap transition-all ${
                  selectedCategory === category
                    ? 'bg-blue-700 text-white'
                    : 'bg-white text-slate-700 border border-slate-300 hover:border-blue-400'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Insights Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Recent Publications</h2>
            <Link
              to="/contact"
              className="text-blue-700 font-medium hover:text-blue-800 transition-colors"
            >
              Subscribe to Updates →
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {insights.map((insight) => (
              <article
                key={insight.id}
                className="bg-white rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300 overflow-hidden group"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                      {insight.category}
                    </span>
                    <div className="flex items-center space-x-2 text-xs text-slate-500">
                      <Clock className="w-3 h-3" />
                      <span>{insight.readTime}</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3 leading-tight group-hover:text-blue-700 transition-colors">
                    {insight.title}
                  </h3>
                  <p className="text-slate-600 mb-4 leading-relaxed line-clamp-3">
                    {insight.excerpt}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                    <div className="flex items-center space-x-2 text-sm text-slate-500">
                      <Calendar className="w-4 h-4" />
                      <span>{insight.date}</span>
                    </div>
                    <Link
                      to="/contact"
                      className="text-blue-700 font-medium text-sm hover:text-blue-800 transition-colors flex items-center"
                    >
                      Read More
                      <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Insights */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-slate-900 mb-8">More Insights</h3>
          <div className="space-y-6">
            {[
              {
                title: 'OPEC+ Production Policy: Implications for Q1 2025 Oil Markets',
                category: 'Oil Markets',
                date: '2024-12-10'
              },
              {
                title: 'European Gas Storage Levels: Winter Supply Security Assessment',
                category: 'Natural Gas',
                date: '2024-12-08'
              },
              {
                title: 'Battery Metal Demand Forecasts: EV Growth Scenarios to 2030',
                category: 'Mining',
                date: '2024-12-05'
              },
              {
                title: 'US Permian Basin Production Outlook: Infrastructure Constraints Analysis',
                category: 'Oil Markets',
                date: '2024-12-03'
              }
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full">
                        {item.category}
                      </span>
                      <span className="text-sm text-slate-500">{item.date}</span>
                    </div>
                    <h4 className="text-lg font-semibold text-slate-900 hover:text-blue-700 transition-colors">
                      {item.title}
                    </h4>
                  </div>
                  <Link
                    to="/contact"
                    className="ml-4 text-blue-700 hover:text-blue-800 transition-colors flex-shrink-0"
                  >
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-gradient-to-r from-[#157D3F] via-[#195C63] to-[#1D3B87]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <BookOpen className="w-16 h-16 text-white/80 mx-auto mb-6" />
          <h2 className="text-4xl font-bold text-white mb-6">
            Stay Informed on Market Developments
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Subscribe to our intelligence briefings and receive timely analysis directly to your inbox
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-[#1D3B87] font-semibold rounded-md hover:bg-slate-100 transition-all duration-200 shadow-lg"
          >
            Subscribe Now
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Insights;
